import { Injectable, inject, computed } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import type { AuthModeInfo, SocialProviderInfo } from '@zag/auth/models';

/**
 * Service for discovering the tenant's available authentication methods.
 *
 * Fetches the auth mode configuration from `GET /api/auth-mode` on
 * initialization and exposes reactive signals that the login/register
 * components use to conditionally render email/password forms and social
 * login buttons.
 *
 * The endpoint is public (no authentication required) because it is
 * called on the login page before the user is authenticated.
 *
 * @example
 * ```typescript
 * private readonly authModeService = inject(AuthModeService);
 *
 * // Check if email/password is available
 * if (this.authModeService.emailPasswordEnabled()) {
 *   // Show email/password form
 * }
 *
 * // Get available social providers
 * const providers = this.authModeService.socialProviders();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AuthModeService {
  private readonly http = inject(HttpClient);

  /** Default auth mode when the endpoint hasn't responded yet. */
  private static readonly DEFAULT_AUTH_MODE: AuthModeInfo = {
    authModes: ['emailPassword'],
    emailPasswordEnabled: true,
    credentialIdentifier: 'email',
    usernamePolicy: null,
    socialProviders: [],
  };

  /**
   * Resource that loads the auth mode configuration.
   *
   * Automatically fetches on service creation. The resource handles
   * loading, error, and success states.
   */
  readonly authModeResource = rxResource<AuthModeInfo, unknown>({
    defaultValue: AuthModeService.DEFAULT_AUTH_MODE,
    stream: () => this.http.get<AuthModeInfo>('/api/auth-mode'),
  });

  /** The resolved auth mode info, falling back to default while loading. */
  readonly authMode = computed<AuthModeInfo>(() => this.authModeResource.value() ?? AuthModeService.DEFAULT_AUTH_MODE);

  /** Whether the email/password login form should be shown. */
  readonly emailPasswordEnabled = computed<boolean>(() => this.authMode().emailPasswordEnabled);

  /** Whether credentials use a local username instead of an email address. */
  readonly usernameEnabled = computed<boolean>(() => this.authMode().credentialIdentifier === 'username');

  /** Username constraints supplied by the tenant configuration. */
  readonly usernamePolicy = computed(() => this.authMode().usernamePolicy);

  /** List of available social login providers. */
  readonly socialProviders = computed<SocialProviderInfo[]>(() => this.authMode().socialProviders);

  /** Whether social login is available (at least one provider configured). */
  readonly hasSocialProviders = computed<boolean>(() => this.socialProviders().length > 0);

  /** Whether the resource is still loading. */
  readonly isLoading = computed<boolean>(() => this.authModeResource.isLoading());

  /** Whether the resource has encountered an error. */
  readonly hasError = computed<boolean>(() => this.authModeResource.error() !== undefined);
}
